// src/components/HubSpotDealPicker.js
//
// v3.5.99.7.15 — Two-step HubSpot picker used by the standalone ETF
// Calculator at /etf-calculator. Replaces the free-text Client Name field
// with structured selection so the ETF letter ends up attached to the
// correct HubSpot deal (otherwise we keep losing these).
//
// UX flow:
//   1. Rep types a few chars of the company name → debounced search via
//      hubspot proxy → dropdown of matches
//   2. Rep picks a company → component fetches deals for that company
//   3. Rep picks a deal (or "no deal — just calculate") → onSelect fires
//
// On select, the parent (ETFCalculatorPage) gets:
//   {
//     companyId, companyName,                                  — always
//     dealId, dealName, dealAmount, dealStage, dealCreated,    — when deal picked
//     contactName, contactEmail,                               — from get_deal_full
//     quoteNumber (i.e. contract reference suggestion),        — derived from dealName
//   }
//
// Picker collapses to a summary line after selection. "Change" link allows
// reset. No global state — all selection state owned by parent.

import { useState, useEffect, useRef } from 'react';
import { searchCompanies, getCompanyDeals, getDealFull } from '../lib/hubspot';

// v3.5.99.7.18.1: Format the HubSpot company address parts into a single-line
// string suitable for the ETF letter form's Client Address field. HubSpot
// stores address pieces in separate fields (address, address2, city, state,
// zip, country) — any of which can be null. Joins the present parts with the
// usual American postal punctuation:
//   "123 Main St, Suite 200, Springfield, IL 62701, USA"
// US country gets stripped (it's the default and just adds noise on a domestic
// letter). Returns empty string when no address fields are populated.
function formatCompanyAddress(company) {
  if (!company) return '';
  const street = [company.address, company.address2].filter(Boolean).join(', ');
  const cityStateZip = [
    company.city,
    [company.state, company.zip].filter(Boolean).join(' '),
  ].filter(Boolean).join(', ');
  const country = company.country && !/^(usa|us|united states)$/i.test(company.country)
    ? company.country
    : '';
  return [street, cityStateZip, country].filter(Boolean).join(', ');
}

export default function HubSpotDealPicker({ onSelect, value }) {
  const [step,            setStep]            = useState('search'); // 'search' → 'deals' → 'done'
  const [query,           setQuery]           = useState('');
  const [searching,       setSearching]       = useState(false);
  const [results,         setResults]         = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [deals,           setDeals]           = useState([]);
  const [loadingDeals,    setLoadingDeals]    = useState(false);
  const [errorMsg,        setErrorMsg]        = useState('');
  const debounceRef = useRef(null);

  // If parent provides a pre-selected value (e.g. on form reset), collapse
  // to the done state. Currently no callers pass `value`, but the prop is
  // here for future re-mount-from-state scenarios.
  useEffect(() => {
    if (value?.companyId && value?.dealId) setStep('done');
  }, [value]);

  // Debounced company search
  useEffect(() => {
    if (step !== 'search') return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!query || query.length < 2) {
      setResults([]);
      return;
    }
    setSearching(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const rows = await searchCompanies(query);
        setResults(rows);
        setErrorMsg('');
      } catch (e) {
        setErrorMsg(e?.message || 'Search failed');
        setResults([]);
      }
      setSearching(false);
    }, 350);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [query, step]);

  async function handlePickCompany(company) {
    setSelectedCompany(company);
    setStep('deals');
    setLoadingDeals(true);
    try {
      const rows = await getCompanyDeals(company.id);
      setDeals(rows);
      setErrorMsg('');
    } catch (e) {
      setErrorMsg(e?.message || 'Deal list failed');
      setDeals([]);
    }
    setLoadingDeals(false);
  }

  async function handlePickDeal(deal) {
    // Fetch full deal context (contact info) before notifying parent
    let contactName = '', contactEmail = '';
    try {
      const full = await getDealFull(deal.id);
      if (full?.contact) {
        const first = full.contact.firstname || '';
        const last  = full.contact.lastname  || '';
        contactName  = `${first} ${last}`.trim() || full.contact.email || '';
        contactEmail = full.contact.email || '';
      }
    } catch (e) {
      console.warn('getDealFull failed (non-fatal):', e?.message || e);
    }

    setStep('done');
    onSelect?.({
      companyId:    selectedCompany.id,
      companyName:  selectedCompany.name,
      // v7.18.1: pre-formatted single-line address (empty string if HubSpot
      // has no address fields populated). Calculator pre-fills this into the
      // Client Address field, but only when that field is empty — so manual
      // edits aren't blown away.
      companyAddress: formatCompanyAddress(selectedCompany),
      dealId:       deal.id,
      dealName:     deal.dealname,
      dealAmount:   deal.amount,
      dealStage:    deal.stage,
      dealCreated:  deal.createdate,
      dealUrl:      deal.url,
      contactName,
      contactEmail,
      // Suggest the deal name as the contract reference. Rep can edit on form.
      quoteNumber:  deal.dealname,
    });
  }

  function handleSkipDeal() {
    setStep('done');
    onSelect?.({
      companyId:    selectedCompany.id,
      companyName:  selectedCompany.name,
      companyAddress: formatCompanyAddress(selectedCompany),
      dealId:       null,
      dealName:     null,
      contactName:  '',
      contactEmail: '',
      quoteNumber:  '',
    });
  }

  function handleReset() {
    setStep('search');
    setQuery('');
    setResults([]);
    setSelectedCompany(null);
    setDeals([]);
    setErrorMsg('');
    onSelect?.(null);
  }

  // ── Step: done (summary line + change link) ──────────────────────────────
  if (step === 'done' && value) {
    return (
      <div style={{ padding:'10px 12px', background:'#f0fdf4', border:'1px solid #bbf7d0', borderRadius:6, display:'flex', alignItems:'center', gap:10, justifyContent:'space-between' }}>
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ fontSize:11, fontWeight:700, color:'#0f1e3c' }}>
            ✓ {value.companyName}
            {value.dealName && <span style={{ color:'#6b7280', fontWeight:400 }}> · {value.dealName}</span>}
          </div>
          {value.contactEmail && (
            <div style={{ fontSize:10, color:'#6b7280', marginTop:2 }}>
              Contact: {value.contactName || value.contactEmail}{value.contactEmail && value.contactName ? ` <${value.contactEmail}>` : ''}
            </div>
          )}
          {!value.dealId && (
            <div style={{ fontSize:10, color:'#92400e', marginTop:2, fontStyle:'italic' }}>
              No deal linked — letter won't auto-upload to HubSpot
            </div>
          )}
        </div>
        <button type="button" onClick={handleReset}
          style={{ fontSize:10, color:'#2563eb', background:'transparent', border:'none', cursor:'pointer', fontWeight:600, textDecoration:'underline' }}>
          Change
        </button>
      </div>
    );
  }

  // ── Step: search (company typeahead) ─────────────────────────────────────
  if (step === 'search') {
    return (
      <div>
        <input
          autoFocus
          type="text"
          value={query}
          onChange={e => setQuery(e.target.value)}
          placeholder="Search HubSpot companies by name (min 2 chars)..."
          style={{ width:'100%', padding:'8px 10px', border:'1px solid #d1d5db', borderRadius:4, fontSize:12, outline:'none', color:'#1f2937', background:'white' }}
        />
        {searching && (
          <div style={{ fontSize:10, color:'#6b7280', marginTop:6, fontStyle:'italic' }}>Searching…</div>
        )}
        {errorMsg && (
          <div style={{ fontSize:10, color:'#991b1b', marginTop:6 }}>{errorMsg}</div>
        )}
        {!searching && query.length >= 2 && results.length === 0 && !errorMsg && (
          <div style={{ fontSize:10, color:'#9ca3af', marginTop:6, fontStyle:'italic' }}>No matches.</div>
        )}
        {results.length > 0 && (
          <div style={{ marginTop:6, border:'1px solid #e5e7eb', borderRadius:4, maxHeight:240, overflowY:'auto', background:'white' }}>
            {results.map(c => (
              <button key={c.id} type="button" onClick={() => handlePickCompany(c)}
                style={{ width:'100%', textAlign:'left', padding:'7px 10px', background:'none', border:'none', borderBottom:'1px solid #f3f4f6', cursor:'pointer', fontSize:11 }}>
                <div style={{ fontWeight:600, color:'#0f1e3c' }}>{c.name}</div>
                <div style={{ fontSize:9, color:'#6b7280', marginTop:1 }}>
                  {[c.domain, c.industry, c.city && c.state ? `${c.city}, ${c.state}` : c.city || c.state].filter(Boolean).join(' · ') || '(no additional info)'}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  // ── Step: deals (deal picker) ────────────────────────────────────────────
  if (step === 'deals') {
    return (
      <div>
        <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:8 }}>
          <button type="button" onClick={handleReset}
            style={{ fontSize:10, color:'#2563eb', background:'transparent', border:'none', cursor:'pointer', fontWeight:600, padding:0 }}>
            ← Back
          </button>
          <span style={{ fontSize:11, color:'#6b7280' }}>
            Closed Won deals for <strong style={{ color:'#0f1e3c' }}>{selectedCompany?.name}</strong>:
          </span>
        </div>
        {loadingDeals && (
          <div style={{ fontSize:10, color:'#6b7280', fontStyle:'italic' }}>Loading deals…</div>
        )}
        {errorMsg && (
          <div style={{ fontSize:10, color:'#991b1b' }}>{errorMsg}</div>
        )}
        {!loadingDeals && deals.length === 0 && !errorMsg && (
          <div style={{ fontSize:10, color:'#9ca3af', fontStyle:'italic', marginBottom:8 }}>No closed-won deals on this company.</div>
        )}
        {deals.length > 0 && (
          <div style={{ border:'1px solid #e5e7eb', borderRadius:4, maxHeight:300, overflowY:'auto', background:'white', marginBottom:8 }}>
            {deals.map(d => (
              <button key={d.id} type="button" onClick={() => handlePickDeal(d)}
                style={{ width:'100%', textAlign:'left', padding:'8px 10px', background:'none', border:'none', borderBottom:'1px solid #f3f4f6', cursor:'pointer', fontSize:11 }}>
                <div style={{ fontWeight:600, color:'#0f1e3c' }}>{d.dealname}</div>
                <div style={{ fontSize:9, color:'#6b7280', marginTop:1 }}>
                  {d.amount ? `$${Number(d.amount).toLocaleString()}` : '—'}
                  {d.stage && <span> · {d.stage}</span>}
                  {/* v3.5.99.7.18.4: Since v7.18.3 filters to Closed Won only,
                      the closedate field represents when the deal was actually
                      won. Display that instead of the (less informative)
                      createdate. Fall back to createdate only if a deal is
                      missing a closedate (defensive — shouldn't happen for
                      Closed Won). */}
                  {d.closedate
                    ? <span> · closed {new Date(d.closedate).toLocaleDateString()}</span>
                    : d.createdate
                      ? <span> · created {new Date(d.createdate).toLocaleDateString()}</span>
                      : null}
                </div>
              </button>
            ))}
          </div>
        )}
        <button type="button" onClick={handleSkipDeal}
          style={{ fontSize:10, color:'#6b7280', background:'transparent', border:'1px dashed #d1d5db', borderRadius:4, padding:'5px 10px', cursor:'pointer', width:'100%', textAlign:'center' }}>
          Use this company without selecting a deal
        </button>
      </div>
    );
  }

  return null;
}
